import { businessOptikTvFieldNames } from '../../../utils/constant';

/**
 * Pure rewrite of `BusinessOptikTvHelpers` (vulcanHelpers/businessOptikTvHelpers.js)
 * as a set of `fieldRules` for the Business Optik TV section — option filtering +
 * visibility/required for Profile Type / Term / Monthly Rate Plan, driven by the
 * Liquor License answer and the selected Profile Type.
 *
 * `formFields[X].value` → `values[X]`; `formFields[X].options` (same field) →
 * `field.options`; `fields.isVisible = …` → `{ visibility: … }`;
 * `fields.isRequired = …` → `{ isRequired: … }`. Verified against the original
 * helper by rules/businessOptikTv.diff.test.js.
 *
 * @param {{t: Object}} ctx  `t` = active-locale `vulcanWebform` copy
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeBusinessOptikTvRule(ctx) {
  const t = (ctx && ctx.t) || {};
  // Guard the string set: a missing `t.businessOptikTv` (e.g. wrong copy passed,
  // or a locale gap) must degrade this rule to a no-op, never white-screen the
  // whole form via the error boundary.
  const {
    privateHomeBasedBusinessOpus,
    publicOpus,
    privateLegacy,
    publicLegacy,
    publicLiquor,
    monthToMonth,
    basicsBtl,
    core,
    choices,
    moreChoices,
    optikTvBasic,
    optikTv,
    optikTvElite
  } = t.businessOptikTv || {};
  const { yes, no } = t;
  const {
    customerLiquorLicenseVul,
    profileTypeOptikTvVul,
    termBusinessOptikTvVul,
    monthlyRatePlanOptikTvVul
  } = businessOptikTvFieldNames;

  return (field, state, values) => {
    const { name } = field;
    // Evolving options (a prior rule may have filtered them), falling back to the
    // field definition — see composeRules' threaded state.
    const options = state.options || field.options || [];
    const liquor = values[customerLiquorLicenseVul];
    const profileType = values[profileTypeOptikTvVul];

    // Profile Type: filtered by the Liquor License answer.
    if (name === profileTypeOptikTvVul) {
      if (liquor === yes) {
        return {
          options: options.filter(o =>
            [publicOpus, publicLegacy, publicLiquor].includes(o.fields.value)
          )
        };
      }
      if (liquor === no) {
        return {
          options: options.filter(
            o => ![publicLegacy, publicLiquor].includes(o.fields.value)
          )
        };
      }
      return { options };
    }

    // Term: options by Profile Type, plus visibility/required for legacy/opus.
    if (name === termBusinessOptikTvVul) {
      const patch = {};
      patch.options =
        profileType === publicLegacy || profileType === privateLegacy
          ? options.filter(o => o.fields.value === monthToMonth)
          : options;
      if (
        [
          publicLegacy,
          privateLegacy,
          privateHomeBasedBusinessOpus,
          publicOpus
        ].includes(profileType)
      ) {
        patch.visibility = true;
        patch.isRequired = true;
      }
      return patch;
    }

    // Monthly Rate Plan: options by Profile Type; shown+required once a Profile
    // Type is chosen, hidden otherwise.
    if (name === monthlyRatePlanOptikTvVul) {
      let updated = options;
      if (profileType === privateLegacy || profileType === publicLegacy) {
        updated = options.filter(o =>
          [optikTvBasic, optikTv, optikTvElite].includes(o.fields.value)
        );
      } else if (
        [privateHomeBasedBusinessOpus, publicOpus, publicLiquor].includes(
          profileType
        )
      ) {
        updated = options.filter(o =>
          [basicsBtl, core, choices, moreChoices].includes(o.fields.value)
        );
      }
      return profileType
        ? { options: updated, visibility: true, isRequired: true }
        : { options: updated, visibility: false, isRequired: false };
    }

    return undefined;
  };
}

export default makeBusinessOptikTvRule;
